import React from "react";
import { queryCache, useQuery } from "react-query";
import { Helmet } from "react-helmet";
import { Theme } from "@material-ui/core/styles";
import Container from "@material-ui/core/Container";
import makeStyles from "@material-ui/core/styles/makeStyles";
import Grid from "@material-ui/core/Grid";
import WeduTooltip from "../../../components/WeduTooltip/intex";
import Loading from "../../../components/Loading";
import { endOfTitle } from "../../../constants/constants";
import Button from "../../../components/Button";
import { AdapterLink } from "../../../components/Adopter";
import Error from "../../../components/Error";
import { mutateErrors } from "../../../utils";
import useBranchQueries from "../../school/hooks/useBranchQueries";
import { useSchoolId } from "../../school/hooks/useSchoolId";
import { useUserAccount } from "../../user/userAccount/context";
import { UserRole } from "../../user/enums/UserRole";
import { useAuth } from "../../auth/hooks/useAuth";
import { useWizard } from "../../wizard/context";
import { useAddBranchConfig } from "../modals/create/config";
import BranchListItem from "./BranchListItem";
import { QUERY_KEYS } from "../../../constants";

const useStyles = makeStyles((theme: Theme) => ({
  root: {},
  container: {
    display: "flex",
    flexDirection: "column",
    padding: 0,
  },
  buttonContainer: {
    marginBottom: theme.spacing(3),
    padding: 0,
    textAlign: "right",
  },
  button: {
    maxWidth: 280,
    fontSize: 16,
  },
}));

export function BranchesPage() {
  const classes = useStyles();
  const { fetchAll } = useBranchQueries();
  const { openWizard } = useWizard();
  const addBranchConfig = useAddBranchConfig();
  const { user } = useAuth();
  const isAdminOrSupport = [
    UserRole.Owner,
    UserRole.Admin,
    UserRole.Support,
  ].includes(user.role);

  const { user: authUser } = useUserAccount();
  const userId = isAdminOrSupport ? authUser?.id : user.id;
  const schoolId = useSchoolId();

  const { userAvailableBranches, branches } = QUERY_KEYS;

  const { isLoading, isError, error, data } = useQuery<any>(
    [userAvailableBranches, userId],
    () => fetchAll({ schoolId }),
  );

  const handleAddBranch = async () => {
    if (await openWizard(addBranchConfig)) {
      await queryCache.invalidateQueries([userAvailableBranches]);
      await queryCache.invalidateQueries(branches);
    }
  };

  if (isError) {
    const { message } = mutateErrors(error);
    return <Error text={message} />;
  }

  return (
    <>
      {isLoading && <Loading />}
      <Helmet>
        <title>Branches{endOfTitle}</title>
      </Helmet>
      <Container maxWidth="lg" className={classes.root}>
        <div className={classes.buttonContainer}>
          <WeduTooltip title="Add Branch">
            <Button
              color={"success"}
              variant={"contained"}
              component={AdapterLink}
              onClick={handleAddBranch}
              className={classes.button}
            >
              Add
            </Button>
          </WeduTooltip>
        </div>
        <div className={classes.container}>
          <Grid container spacing={2}>
            {data &&
              data.data.map((branch: any) => (
                <BranchListItem
                  key={`BranchListItem_${branch.id}`}
                  branch={branch}
                />
              ))}
          </Grid>
        </div>
      </Container>
    </>
  );
}

export default BranchesPage;
